import { Suspense } from "react";

import { CookieConsentBanner } from "@/components/analytics/CookieConsentBanner";
import { clerkShadcnSafeColorVariables } from "@/components/auth/clerkShadcnThemeOverrides";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ChecklistLeaveGuardProvider } from "@/contexts/ChecklistLeaveGuardContext";
import { PageGuideProvider } from "@/contexts/PageGuideContext";
import { TodayRenameNavHighlightProvider } from "@/contexts/TodayRenameNavHighlightContext";
import { WelcomeGuideProvider } from "@/contexts/WelcomeGuideContext";
import { PostHogAnalyticsConsentSync } from "@/features/analytics/PostHogAnalyticsConsentSync";
import { ApiClientProvider } from "@/features/api/ApiClientProvider";
import { MANAGE_MFA_PATH } from "@/features/auth/orgMfa";
import { ThemeProvider } from "@/hooks/useTheme";
import { AppRoutes } from "@/routes/AppRoutes";
import { lazyPage } from "@/routes/lazyPage";
import { PreloadRouteBundles } from "@/routes/PreloadRouteBundles";

import { ClerkProvider } from "@clerk/react";
import { QueryClientProvider } from "@tanstack/react-query";

import ConfigurePostHog from "./features/analytics/ConfigurePostHog";
import { queryClient } from "./lib/queryClient";

const AuthenticatedAppEnhancements = lazyPage(
  () => import("@/components/AuthenticatedAppEnhancements")
);

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;

// Clerk raises `choose-organization` for any session without an active
// organisation, which includes brand-new self-serve sign-ups. The route below
// forks between invitation acceptance and the onboarding wizard.
// `setup-mfa` is raised when Clerk itself requires a second factor; it shares
// the manage-MFA page with the organisation-level policy guard (ASA-1515).
const CLERK_TASK_URLS = {
  "choose-organization": "/choose-organization",
  "setup-mfa": MANAGE_MFA_PATH,
};

if (!PUBLISHABLE_KEY) {
  throw new Error("Add your Clerk Publishable Key to the .env file");
}

function App() {
  return (
    <ConfigurePostHog>
      <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        afterSignOutUrl="/"
        taskUrls={CLERK_TASK_URLS}
        appearance={{
          variables: {
            ...clerkShadcnSafeColorVariables,
            borderRadius: "var(--radius)",
          },
          elements: {
            // Ensure consistent styling and faster loading
            rootBox: "mx-auto",
          },
        }}
      >
        <ThemeProvider>
          <ApiClientProvider>
            <QueryClientProvider client={queryClient}>
              <TooltipProvider>
                <WelcomeGuideProvider>
                  <TodayRenameNavHighlightProvider>
                    <PageGuideProvider>
                      <PostHogAnalyticsConsentSync />
                      <CookieConsentBanner />
                      <Suspense fallback={null}>
                        <AuthenticatedAppEnhancements />
                      </Suspense>
                      <Toaster />
                      <SonnerToaster />
                      <ChecklistLeaveGuardProvider>
                        <PreloadRouteBundles />
                        <AppRoutes />
                      </ChecklistLeaveGuardProvider>
                    </PageGuideProvider>
                  </TodayRenameNavHighlightProvider>
                </WelcomeGuideProvider>
              </TooltipProvider>
            </QueryClientProvider>
          </ApiClientProvider>
        </ThemeProvider>
      </ClerkProvider>
    </ConfigurePostHog>
  );
}

export default App;
